import { useEffect } from 'react'

import {
  TagContentTypeEnum,
  getTagContentTypeString,
  tagByIdQuery,
  tagContentTypeArray,
} from '@/entities/tags'
import { confirm } from '@/shared/lib/antd-utils'
import { hasFormChanged } from '@/shared/lib/form-utils'
import { showNotificationWarningFx } from '@/shared/lib/providers'
import { HidingSpin, SelfValidateInput } from '@/shared/ui'
import { PrimaryButton } from '@/shared/ui'
import { Flex, Form, Modal, Radio } from 'antd'
import { useGate, useUnit } from 'effector-react'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { MAX_FIELD_LENGTH } from './lib/constants'
import { getTagSubmitRequestByFormValues } from './lib/get-tag-submit-request'
import {
  $isTagFormModalOpened,
  $submittingTag,
  TagFormGate,
  tagFormModalClosed,
  tagSubmitted,
} from './model/tag-form.model'

export { tagFormModalOpened } from './model/tag-form.model'

const tagSchema = z.object({
  name: z.string(),
  contentType: z.custom<TagContentTypeEnum>(),
})

export type TagFormModel = z.infer<typeof tagSchema>

interface TagFormProps {
  isModal?: boolean
  onSubmitted?: (tagId: number) => void
  defaultContentType?: TagContentTypeEnum
}
export const TagForm = ({
  isModal,
  onSubmitted,
  defaultContentType,
}: TagFormProps) => {
  //   Queries
  const {
    data: tagData,
    pending: tagPending,
    error: tagError,
  } = useUnit(tagByIdQuery)
  const tagQueryStatus = useUnit(tagByIdQuery.$status)

  const submitTag = useUnit(tagSubmitted)
  const submitting = useUnit($submittingTag)
  //   Modal
  const isModalOpened = useUnit($isTagFormModalOpened)
  const closeFormModal = useUnit(tagFormModalClosed)
  //   Form state
  const formik = useFormik<TagFormModel>({
    enableReinitialize: true,
    initialValues: tagData
      ? {
          name: tagData.name,
          contentType: tagData.contentType,
        }
      : {
          name: '',
          contentType: defaultContentType ?? tagContentTypeArray[0],
        },
    validationSchema: toFormikValidationSchema(tagSchema),
    onSubmit: (values: TagFormModel, { setSubmitting }) => {
      const tagSubmittedData = getTagSubmitRequestByFormValues(values)
      submitTag(tagSubmittedData)
      setSubmitting(false)
    },
  })

  useGate(TagFormGate, {
    onSubmitted,
    isModal,
    resetForm: formik.resetForm,
  })

  const { isValid, isValidating, isSubmitting } = formik
  useEffect(() => {
    if (!isValid && !isValidating && isSubmitting) {
      void showNotificationWarningFx({ message: 'Не все поля заполнены' })
    }
  }, [isSubmitting, isValid, isValidating])

  // Prevent data lose when try to close

  const isDirty = hasFormChanged(formik.values, formik.initialValues)

  useEffect(() => {
    if (isDirty) {
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault()
      }
      window.addEventListener('beforeunload', handler)
      return () => {
        window.removeEventListener('beforeunload', handler)
      }
    }
  }, [isDirty])
  const tryToClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: 'Данные не будут сохранены',
        content: 'Вы уверены, что хотите закрыть форму?',
        okText: 'Да, закрыть',
        cancelText: 'Отмена',
        centered: true,
      })
      if (!confirmed) return
    }
    formik.resetForm()
    closeFormModal()
  }

  const formControlButtonsDisabled =
    !hasFormChanged(
      formik.values,
      formik.initialValues,
      !tagData ? ['contentType'] : [],
    ) || submitting

  const formComponent = (
    <HidingSpin
      spinning={
        tagQueryStatus !== 'initial' && (tagPending || (!tagData && !tagError))
      }
    >
      <Form.Item layout="vertical" label="Название" required>
        <SelfValidateInput
          name="name"
          showCount
          maxLength={MAX_FIELD_LENGTH.name}
          value={formik.values.name}
          onChange={(value) => {
            void formik.setFieldValue('name', value)
          }}
        />
      </Form.Item>

      <Form.Item label="Тип контента" layout="vertical" required>
        <Radio.Group
          name="contentType"
          value={formik.values.contentType}
          options={tagContentTypeArray
            .filter(
              (tagContentType) => tagContentType !== TagContentTypeEnum.Album,
            )
            .map((tagContentType) => ({
              value: tagContentType,
              label: getTagContentTypeString(tagContentType),
            }))}
          onChange={(value) => {
            const newContentType: TagContentTypeEnum = value.target.value
            void formik.setFieldValue('contentType', newContentType)
          }}
        />
      </Form.Item>
      <Flex justify="flex-end" gap={10}>
        <PrimaryButton
          disabled={formControlButtonsDisabled}
          onClick={() => {
            formik.handleSubmit()
          }}
          loading={submitting}
        >
          {tagData ? 'Сохранить' : 'Создать'}
        </PrimaryButton>
      </Flex>
    </HidingSpin>
  )

  return (
    <>
      {isModal ? (
        <Modal
          open={isModalOpened}
          onCancel={() => {
            void tryToClose()
          }}
          destroyOnHidden
          centered
          title={
            tagQueryStatus === 'initial'
              ? 'Создание жанра'
              : tagPending || (!tagData && !tagError)
                ? 'Загрузка'
                : 'Редактирование жанра'
          }
          footer={null}
        >
          {formComponent}
        </Modal>
      ) : (
        formComponent
      )}
    </>
  )
}
